<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { EntityVacancyItem, EntityVacancySkeleton } from '~/components/entity/vacancy'
import type { IVacancyCard, IVacancyListProps as IProps } from '~/components/entity/vacancy'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'

const config = useRuntimeConfig()
const props = defineProps<IProps>()

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

const hitsPerPage = Number(props.queryParams?.per_page || 10)

const searchQueryString = ref(String(route.query.q || ''))
const currentPage = ref(Number(route.query.page) || 1)
const debouncedQuery = useDebounce(searchQueryString, 500)

const searchKey = computed(() => `vacancies-search-${debouncedQuery.value}-${currentPage.value}`)

const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IVacancyCard> | null>(
  searchKey,
  async () => {
    const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_vacancies`)

    try {
      return await search(debouncedQuery.value, {
        hitsPerPage,
        page: currentPage.value,
        sort: ['updated_at:desc'],
        attributesToHighlight: ['title']
      })
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    watch: false,
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

const typedHits = computed<IVacancyCard[]>(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    url: hit.url || '',
    updated_at: hit.updated_at,
    location: {
      ...hit.location,
      title: hit.location.title[locale.value]
    },
    organization: {
      title: hit.organization.title[locale.value] || t('entity.vacancy.no_title'),
      url: hit.organization.url || '#',
      logo: hit.logo || ''
    }
  }))
})

// Initialize component
const isInitializing = ref(true)

onMounted(() => {
  isInitializing.value = false
})

// Watch for changes and refresh data
watch(
  [debouncedQuery, currentPage],
  async () => {
    if (isInitializing.value) return

    router.replace({
      path: route.path,
      query: {
        ...route.query,
        q: debouncedQuery.value || undefined,
        page: currentPage.value > 1 ? String(currentPage.value) : undefined
      }
    })
  },
  { deep: true }
)

const changePage = (page: number) => {
  currentPage.value = page
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />

      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="searchQueryString = ''"
      >
        <LucideCircleX />
      </Button>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="vacancies">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="vacancies" tag="h2" class="sr-only">{{ $t('common.vacancies') }}</Heading>
        <template v-for="item in typedHits" :key="item.id">
          <EntityVacancyItem :item="item" :hide-organization-details="false" />
        </template>

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.no_open_vacancies') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityVacancySkeleton
        :count="Number(props.queryParams?.per_page || 3)"
        :hide-organization-details="false"
      />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('widget.vacancies.list_meili_search.error_title') }}</AlertTitle>
        <AlertDescription>
          {{ $t('widget.vacancies.list_meili_search.error_description') }}
        </AlertDescription>
      </Alert>
    </section>
  </div>
</template>
